<template>
  <v-menu offset-y :close-on-content-click="false" v-model="menuOpen">
    <template #activator="{ props }">
      <div class="d-flex flex-column align-center" style="cursor: pointer" v-bind="props">
        <v-badge :content="unreadCount"  class="small-badge">
          <v-icon size="small">mdi-bell-outline</v-icon>
        </v-badge>
        <slot></slot>
      </div>
    </template>

    <v-card width="450">
      <v-list class="pa-0">
        <v-list-item class="px-7 pt-3 pb-0">
          <v-row>
            <v-col cols="7">
              <span class="text-subtitle-1 font-weight-bold">Notifications</span>
            </v-col>
            <v-col cols="5">
              <span @click="markAllRead" class="text-caption text-primary cursor-pointer"
                >Mark all as Read</span
              >
            </v-col>
          </v-row>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <template v-if="notifications.length">
          <template v-for="(note, index) in notifications" :key="note.id">
            <v-list-item class="px-4" :class="{ 'bg-blue-lighten-5': !note.isRead }">
              <v-row>
                <v-col
                  cols="10"
                  class="d-flex align-center justify-lg-space-between ga-3"
                  style="cursor: pointer"
                  @click="goTo(note)"
                >
                  <v-avatar size="80">
                    <v-img
                      
    
                      :src="note.image ? baseUrl + note.image : defaultIcon"
                    />
                  </v-avatar>

                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-medium">{{ note.title }}</div>
                    <div class="text-caption">{{ note.body }}</div>
                    <small class="text-grey">{{ formatDate(note.date) }}</small>
                  </div>
                </v-col>

                <v-col cols="2" class="d-flex align-center justify-end">
                  <v-menu bottom left style="margin-right: 0" :close-on-content-click="false">
                    <template #activator="{ props }">
                      <v-btn icon v-bind="props" variant="plain">
                        <v-icon>mdi-dots-horizontal</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item v-if="!note.isRead" @click="markAsRead(note)">
                        <v-list-item-title>Mark as Read</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="removeNotification(note)">
                        <v-list-item-title>Remove Notification</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>
            </v-list-item>
          </template>
        </template>

        <template v-else>
          <v-list-item class="d-flex flex-column align-center justify-center py-6">
            <div style="display: flex; justify-content-center" class="text-center">
              <v-img :src="emptyImage" width="70" class="mb-3" />
            </div>
            <div class="text-subtitle-2 font-weight-bold mb-1 flex text-center">
              No notifications…yet!
            </div>
            <div class="text-caption text-center">
              View ad recommendations and news<br />by suuq, etc..
            </div>
          </v-list-item>
        </template>
      </v-list>

      <v-divider></v-divider>

      <v-list-item  @click="goToAll"  class="text-center  font-weight-bold">
       <span class="notification-all">View All Notifications</span> 
      </v-list-item>
    </v-card>
  </v-menu>
</template>

<script setup>
import { API_URL } from '@/helpers/api';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

// State & refs
const defaultIcon = require('@/assets/bell.png');
const emptyImage = require('@/assets/empynow.png');
const menuOpen = ref(false);

const store = useStore();
const router = useRouter();

// Computed properties
const notifications = computed(() => store.getters['notifications/all']);
const unreadCount = computed(() => store.getters['notifications/unreadCount']);
const loggedInUser = computed(() => store.state.auth.user);
const apiUrl = API_URL;
const baseUrl = process.env.VUE_APP_BASE_URL || '';
/**
 * ✅ ENHANCED: Better listing type detection
 */
const detectListingType = (listing) => {
  if (!listing) return 'general';

  // Check category ID (property categories)
  const categoryId = listing.category_id;
  if (categoryId === 3 || categoryId === 4) return 'property';

  return 'general';
};

// Lifecycle
onMounted(() => {
  // Only initialize notifications if user is authenticated
  if (loggedInUser.value) {
    store.dispatch('notifications/listen');
  }
});

const normalizeListing = (listing) => {
  if (!listing) return null;

  const baseUrl = process.env.VUE_APP_BASE_URL || '';

  // Normalize image URLs
  let normalizedImages = [];

  // Handle image_urls
  if (listing.image_urls && Array.isArray(listing.image_urls)) {
    normalizedImages = listing.image_urls
      .map((url) => {
        if (typeof url === 'string' && url.trim() && url !== '[') {
          if (url.startsWith('/storage/') || url.startsWith('/')) {
            return `${baseUrl}${url}`;
          }
          if (url.startsWith('http')) {
            return url;
          }
        }
        return null;
      })
      .filter(Boolean);
  }

  // Handle images array
  if (!normalizedImages.length && listing.images && Array.isArray(listing.images)) {
    normalizedImages = listing.images
      .map((img) => {
        if (typeof img === 'string' && img.trim() && img !== '[') {
          if (img.startsWith('/storage/') || img.startsWith('/')) {
            return `${baseUrl}${img}`;
          }
          if (img.startsWith('http')) {
            return img;
          }
        }
        return null;
      })
      .filter(Boolean);
  }

  // Handle files array
  if (!normalizedImages.length && listing.files && Array.isArray(listing.files)) {
    normalizedImages = listing.files
      .map((file) => {
        const url = file.url || file.file_path;
        if (url && typeof url === 'string' && url.trim() && url !== '[') {
          if (url.startsWith('/storage/') || url.startsWith('/')) {
            return `${baseUrl}${url}`;
          }
          if (url.startsWith('http')) {
            return url;
          }
        }
        return null;
      })
      .filter(Boolean);
  }

  return {
    ...listing,
    image_urls: normalizedImages,
    category_id: listing.category_id,
  };
};

// Methods
// async function goTo(note) {
//   console.log("Navigating to notification:", note);
//   const listingType = detectListingType(note);

//   // Correct: use note as the listing object
//   const normalizedListing = normalizeListing(note);

//   const source = "notification";
//   const slug = note.slug;

//   // Store in relevant stores
//   if (listingType === "property") {
//     await store.dispatch(
//       "propertyFilter/setSelectedProperty",
//       normalizedListing
//     );
//   }
//   // General store
//   await store.dispatch("FilterStore/setSelectedListingItem", normalizedListing);

//   // Build route config
//   const routeConfig = {
//     name: "ListingDetails",
//     params: {
//       id: note.id,
//       slug: note.slug,
//     },
//     query: {
//       source: source,
//       category: listingType,
//     },
//   };
//   // Actually navigate!
//   router.push(routeConfig);

//   menuOpen.value = false; // Close the menu
// }

async function goTo(note) {
  console.log('Navigating to notification:', note);
  const listingType = detectListingType(note);

  const normalizedListing = normalizeListing(note);
  const source = 'notification';
  const slug = note.slug;

  if (listingType === 'property') {
    await store.dispatch('propertyFilter/setSelectedProperty', normalizedListing);
  }
  await store.dispatch('FilterStore/setSelectedListingItem', normalizedListing);

  // Decide route name based on category_id
  const routeName = note.category_id === 2 ? 'ListingDetailsForJobs' : 'ListingDetails';

  // Set id param: use job_id for jobs, otherwise use listing id
  const idParam = note.category_id === 2 && note.job_id ? note.job_id : note.id;

  const routeConfig = {
    name: routeName,
    params: {
      id: idParam,
      slug: note.slug,
    },
    query: {
      source: source,
      category: listingType,
    },
  };

  router.push(routeConfig);

  menuOpen.value = false;
}

function goToAll() {
  router.push('/notifications');
  menuOpen.value = false;
}

function markAllRead() {
  store.dispatch('notifications/clear');
}

function markAsRead(note) {
  store.dispatch('notifications/markAsRead', note.docId);
}

function removeNotification(note) {
  store.dispatch('notifications/remove', note.docId);
}

function formatDate(date) {
  const now = new Date();
  const d = new Date(date);
  const diff = (now - d) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return Math.floor(diff / 60) + ' min ago';
  if (diff < 86400) return Math.floor(diff / 3600) + ' hours ago';
  return Math.floor(diff / 86400) + ' days ago';
}
</script>

<style scoped>
.small-badge :deep(.v-badge__badge) {
  font-size: 10px;
  height: 16px;
  min-width: 16px;
  padding: 0 4px;
  background: darkgoldenrod !important;

}

.notification-all{
  color: darkgoldenrod !important;
}
</style>
